import {
  BitkitBadge,
  BitkitDefinitionTooltip,
  BitkitExpandableCard,
  BitkitLink,
  IconSparkle,
  IconWarningYellow,
} from '@bitrise/bitkit-v2';
import { Box } from '@chakra-ui/react/box';
import { type ReactNode, useEffect, useRef } from 'react';
import { create } from 'zustand';
import { combine } from 'zustand/middleware';

import { useAccountFeatureFlag } from '@/components/page/GlobalPropsContext';
import { useIsFullSidebarVisibleRefresh } from '@/components/page/PageLayout/useIsFullSidebarVisible';
import { appSettingsPath } from '@/utils/monolithApiRouteService';

import AppPageProps, { AIFeatureStatus } from '../types/AppPageProps';

// Figma sizes the status indicator as a 16px box (matching the warning icon) so the label's
// left edge lines up across every row, regardless of dot / ring / warning state. The 2px top
// margin keeps the dot aligned to the first text line when a long feature name wraps.
const StatusDot = ({ children }: { children: ReactNode }) => (
  <Box display="flex" alignItems="center" justifyContent="center" width="16" height="16" marginBlockStart="2">
    {children}
  </Box>
);

const AIAgentsCardItem = ({ label, status }: { label: string; status: AIFeatureStatus }) => {
  const enabledDot = (
    <StatusDot>
      <Box borderRadius="8" width="8" height="8" background="color/green/bold" />
    </StatusDot>
  );
  const disabledDot = (
    <StatusDot>
      <Box borderRadius="8" width="8" height="8" border="1px solid" borderColor="border/regular" />
    </StatusDot>
  );
  const body = () => {
    if (status === 'credit-limit-reached') {
      return (
        <BitkitDefinitionTooltip method="hover" placement="bottom" text="AI credit limit reached" textSize="md">
          {label}
        </BitkitDefinitionTooltip>
      );
    }
    if (status === 'unknown-error') {
      return (
        <BitkitDefinitionTooltip method="hover" placement="bottom" text="Unknown error" textSize="md">
          {label}
        </BitkitDefinitionTooltip>
      );
    }
    if (status === 'connection-error') {
      return (
        <BitkitDefinitionTooltip method="hover" placement="bottom" text="Connection failed" textSize="md">
          {label}
        </BitkitDefinitionTooltip>
      );
    }
    return label;
  };
  return (
    <Box
      textStyle="body/md/regular"
      color={status === false ? 'text/tertiary' : undefined}
      display="flex"
      flexDirection="row"
      alignItems="flex-start"
      gap="8"
    >
      {status === true && enabledDot}
      {status === false && disabledDot}
      {typeof status === 'string' && (
        <StatusDot>
          <IconWarningYellow aria-hidden size="16" />
        </StatusDot>
      )}
      {body()}
    </Box>
  );
};
const getAgentCounts = (ai: NonNullable<AppPageProps['ai']>, flags: { isCiConfigExpertAgentAvailable: boolean }) => {
  const agents = [
    { enabled: ai.summary, visible: true },
    { enabled: ai.fixer, visible: true },
    { enabled: ai.codeReviewer, visible: true },
    { enabled: ai.ciConfigExpert, visible: flags.isCiConfigExpertAgentAvailable },
  ].filter((a) => a.visible);

  return {
    total: agents.length,
    enabledCount: agents.filter((a) => a.enabled === true).length,
    hasError: agents.some((a) => typeof a.enabled === 'string'),
  };
};

const useIsOpen = create(combine({ open: false }, (set, get) => ({ onToggle: () => set({ open: !get().open }) })));
const AIAgentsCard = ({
  ai,
  slug,
  canChangeSettings,
}: {
  ai: AppPageProps['ai'];
  slug: string;
  canChangeSettings: boolean;
}) => {
  const isCiConfigExpertAgentAvailable = useAccountFeatureFlag('isCiConfigExpertAgentAvailable');

  const { refresh } = useIsFullSidebarVisibleRefresh();
  const { open, onToggle } = useIsOpen();

  // The card's expand/collapse animates its height. Re-measure the sidebar once the size settles
  // (debounced trailing edge), covering both directions — BitkitExpandableCard has no enter-complete
  // callback, so we observe the element instead of wiring onChange/onExitComplete.
  const cardRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const element = cardRef.current;
    if (!element) return undefined;
    let timeout: ReturnType<typeof setTimeout>;
    const observer = new ResizeObserver(() => {
      clearTimeout(timeout);
      timeout = setTimeout(refresh, 100);
    });
    observer.observe(element);
    return () => {
      clearTimeout(timeout);
      observer.disconnect();
    };
  }, [refresh]);

  if (!ai) return null;
  const { total, enabledCount, hasError } = getAgentCounts(ai, { isCiConfigExpertAgentAvailable });
  return (
    <BitkitExpandableCard
      ref={cardRef}
      elevation={false}
      size="md"
      marginInline="16"
      icon={IconSparkle}
      iconColor="icon/secondary"
      title="Agents"
      suffix={
        hasError ? (
          <IconWarningYellow role="img" aria-hidden={false} aria-label="Some agents have errors" size="16" />
        ) : (
          <BitkitBadge variant="subtle" colorVariant="neutral">{`${enabledCount}/${total}`}</BitkitBadge>
        )
      }
      expanded={open}
      onChange={onToggle}
      footer={
        canChangeSettings ? (
          <BitkitLink colorVariant="purple" textStyle="body/sm/regular" href={appSettingsPath(slug, 'ai')}>
            Change settings
          </BitkitLink>
        ) : undefined
      }
    >
      <Box display="flex" flexDirection="column" gap="8">
        <AIAgentsCardItem label="Summary" status={ai.summary} />
        <AIAgentsCardItem label="Build fixer" status={ai.fixer} />
        <AIAgentsCardItem label="Code reviewer" status={ai.codeReviewer} />
        {isCiConfigExpertAgentAvailable && (
          <AIAgentsCardItem label="Configuration generator" status={ai.ciConfigExpert} />
        )}
      </Box>
    </BitkitExpandableCard>
  );
};

export default AIAgentsCard;
